import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from './NavigationContext';
import { useSwipeBack } from './useSwipeBack';

/**
 * PWANavigationManager
 * 
 * Handles PWA-specific navigation concerns:
 * - Prevents accidental app close on Android back button
 * - Manages history stack for standalone mode
 * - Handles iOS Safari standalone limitations
 * - Provides scroll restoration between routes
 * - Manages popstate events for overlay dismissal
 * 
 * This component should be rendered once at the app root level.
 */
function PWANavigationManager({ children }) {
  const location = useLocation();
  const { closeTopOverlay, hasOpenOverlay, saveScrollPosition, restoreScrollPosition } = useNavigation();
  const prevLocationRef = useRef(location);
  const historyLengthRef = useRef(1);
  const isStandaloneRef = useRef(false);

  // Detect standalone PWA mode
  useEffect(() => {
    isStandaloneRef.current =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
  }, []);

  // Core popstate handler for overlay dismissal
  useEffect(() => {
    const handlePopState = (e) => {
      // If there's an open overlay, close it instead of navigating
      if (hasOpenOverlay()) {
        e.preventDefault();
        closeTopOverlay();
        // Don't re-push state here — the overlay registration already pushed one
        return;
      }

      // In standalone mode, prevent app from closing if at root
      if (isStandaloneRef.current && historyLengthRef.current <= 1) {
        try { window.history.pushState({ pwaRoot: true }, ''); } catch (e) { /* ignore */ }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [closeTopOverlay, hasOpenOverlay]);

  // Track history depth
  useEffect(() => {
    if (location.pathname !== prevLocationRef.current.pathname) {
      historyLengthRef.current += 1;
    }
    prevLocationRef.current = location;
  }, [location]);

  // Scroll restoration on route change
  useEffect(() => {
    // Save scroll position of previous route
    saveScrollPosition(prevLocationRef.current.pathname);

    // Restore scroll position of new route (or scroll to top)
    const timer = requestAnimationFrame(() => {
      restoreScrollPosition(location.pathname);
    });

    return () => cancelAnimationFrame(timer);
  }, [location.pathname, saveScrollPosition, restoreScrollPosition]);

  // Prevent full page reloads in standalone mode
  useEffect(() => {
    if (!isStandaloneRef.current) return;

    const handleClick = (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Only intercept internal links
      const isInternal = href.startsWith('/') || href.startsWith(window.location.origin);
      const isNewTab = anchor.target === '_blank';

      if (isInternal && !isNewTab) {
        e.preventDefault();
        // Let React Router handle it — this prevents full reload in PWA
        try { window.history.pushState({}, '', href); } catch (e2) { /* ignore */ }
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Initial history entry to prevent app close
  useEffect(() => {
    if (isStandaloneRef.current) {
      try { window.history.pushState({ pwaRoot: true }, ''); } catch (e) { /* ignore */ }
    }
  }, []);

  // iOS swipe-back gesture support for standalone mode
  const handleSwipeBack = useCallback(() => {
    if (hasOpenOverlay()) {
      closeTopOverlay();
    } else if (window.history.length > 1) {
      window.history.back();
    }
  }, [hasOpenOverlay, closeTopOverlay]);

  useSwipeBack({
    onSwipeBack: handleSwipeBack,
    enabled: true,
  });

  return children || null;
}

/**
 * useScrollRestoration
 * 
 * Hook for manual scroll restoration on specific elements.
 * Useful for scrollable containers that aren't the window.
 */
export function useScrollRestoration(key) {
  const scrollRef = useRef(null);
  const positionsRef = useRef({});

  const save = useCallback(() => {
    if (scrollRef.current) {
      positionsRef.current[key] = scrollRef.current.scrollTop;
    }
  }, [key]);

  const restore = useCallback(() => {
    if (scrollRef.current && positionsRef.current[key] !== undefined) {
      scrollRef.current.scrollTop = positionsRef.current[key];
    }
  }, [key]);

  return { scrollRef, save, restore };
}

export default PWANavigationManager;
